import { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '../api';
import { useToast, Modal, ConfirmModal, useIsAdmin, formatDate } from '../components/shared';

const STAGES = [
  { key: 'new', label: 'جديدة', color: 'var(--text3)' },
  { key: 'contacted', label: 'تواصل', color: 'var(--primary)' },
  { key: 'quoted', label: 'عرض سعر', color: 'var(--yellow)' },
  { key: 'won', label: 'كسب', color: 'var(--green)' },
  { key: 'lost', label: 'خسارة', color: 'var(--red)' },
];
const OPP_TYPES = { maintenance: 'صيانة دورية', partnership: 'تعاون', supply: 'توريد', other: 'أخرى' };
const ORG_TYPES = { clinic: 'مركز', hospital: 'مستشفى', doctor: 'دكتور', company: 'شركة', other: 'أخرى' };
const SOURCES = ['إحالة', 'إعلان', 'زيارة', 'واتساب', 'معرض', 'أخرى'];
const NOTE_KINDS = { note: '📝', call: '📞', visit: '🚗', whatsapp: '💬', system: '⚙️' };

const today = () => new Date().toISOString().slice(0, 10);
const money = v => (v || v === 0) ? `${Number(v).toLocaleString('en')} EGP` : '—';
const isOverdue = o => o.next_followup && !['won', 'lost'].includes(o.stage) && o.next_followup.slice(0, 10) < today();
const isToday = o => o.next_followup && !['won', 'lost'].includes(o.stage) && o.next_followup.slice(0, 10) === today();
const me = () => localStorage.getItem('pf_username') || '';

function OppForm({ initial = {}, clients, owners, onSave, onClose }) {
  const [form, setForm] = useState({
    title: '', org_name: '', org_type: 'clinic', client_id: '', contact_phone: '', opp_type: 'maintenance',
    expected_value: '', owner: me(), owner_pct: '', source: '', next_followup: '', notes: '', ...initial,
  });
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  function pickClient(e) {
    const c = clients.find(x => x.id === e.target.value);
    setForm(f => ({ ...f, client_id: e.target.value, org_name: c ? c.name : f.org_name, org_type: c?.type || f.org_type, contact_phone: f.contact_phone || c?.phone || '' }));
  }

  async function submit() {
    if (!form.title) return toast('العنوان مطلوب', 'error');
    setLoading(true);
    const data = { ...form };
    ['expected_value', 'owner_pct'].forEach(k => { data[k] = data[k] === '' || data[k] === null ? null : Number(data[k]); });
    try { await onSave(data); }
    catch (e) { toast(e.message, 'error'); }
    finally { setLoading(false); }
  }

  return (
    <Modal title={initial.id ? 'تعديل فرصة' : 'فرصة جديدة'} onClose={onClose} footer={
      <>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>إلغاء</button>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={loading}>{loading ? '...' : 'حفظ'}</button>
      </>
    }>
      <div dir="rtl">
        <div className="field"><label>العنوان *</label>
          <input className="input" value={form.title} onChange={set('title')} placeholder="مثال: عقد صيانة مركز النور" autoFocus /></div>
        <div className="grid-2">
          <div className="field"><label>عميل موجود</label>
            <select className="select" value={form.client_id || ''} onChange={pickClient}>
              <option value="">— جهة جديدة —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></div>
          <div className="field"><label>اسم الجهة</label>
            <input className="input" value={form.org_name || ''} onChange={set('org_name')} /></div>
        </div>
        <div className="grid-2">
          <div className="field"><label>نوع الجهة</label>
            <select className="select" value={form.org_type || 'clinic'} onChange={set('org_type')}>
              {Object.entries(ORG_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <div className="field"><label>تليفون</label>
            <input className="input" value={form.contact_phone || ''} onChange={set('contact_phone')} placeholder="01xxxxxxxxx" /></div>
        </div>
        <div className="grid-2">
          <div className="field"><label>نوع الفرصة</label>
            <select className="select" value={form.opp_type || 'maintenance'} onChange={set('opp_type')}>
              {Object.entries(OPP_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <div className="field"><label>القيمة المتوقعة (EGP)</label>
            <input className="input" type="number" value={form.expected_value ?? ''} onChange={set('expected_value')} /></div>
        </div>
        <div className="grid-2">
          <div className="field"><label>المسؤول</label>
            <input className="input" list="opp-owners" value={form.owner || ''} onChange={set('owner')} />
            <datalist id="opp-owners">{owners.map(o => <option key={o} value={o} />)}</datalist></div>
          <div className="field"><label>نسبة المسؤول %</label>
            <input className="input" type="number" min="0" max="100" value={form.owner_pct ?? ''} onChange={set('owner_pct')} /></div>
        </div>
        <div className="grid-2">
          <div className="field"><label>المصدر</label>
            <input className="input" list="opp-sources" value={form.source || ''} onChange={set('source')} />
            <datalist id="opp-sources">{SOURCES.map(s => <option key={s} value={s} />)}</datalist></div>
          <div className="field"><label>المتابعة القادمة</label>
            <input className="input" type="date" value={(form.next_followup || '').slice(0, 10)} onChange={set('next_followup')} /></div>
        </div>
        <div className="field"><label>ملاحظات</label>
          <textarea className="input" rows={2} value={form.notes || ''} onChange={set('notes')} /></div>
      </div>
    </Modal>
  );
}

function OppDetail({ id, onClose, onChanged, onEdit, isAdmin }) {
  const [o, setO] = useState(null);
  const [note, setNote] = useState('');
  const [kind, setKind] = useState('note');
  const [lostAsk, setLostAsk] = useState(false);
  const [lostReason, setLostReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const toast = useToast();

  const load = useCallback(() => api.getOpportunity(id).then(setO).catch(e => toast(e.message, 'error')), [id, toast]);
  useEffect(() => { load(); }, [load]);

  async function update(data) {
    try { await api.updateOpportunity(id, { ...data, author: me() }); await load(); onChanged(); }
    catch (e) { toast(e.message, 'error'); }
  }

  async function moveTo(stage) {
    if (stage === 'lost') return setLostAsk(true);
    update({ stage, lost_reason: '' });
  }

  async function addNote() {
    if (!note.trim()) return;
    try { await api.addOpportunityNote(id, { body: note.trim(), kind, author: me() }); setNote(''); load(); onChanged(); }
    catch (e) { toast(e.message, 'error'); }
  }

  async function remove() {
    await api.deleteOpportunity(id); toast('اتحذفت'); onChanged(); onClose();
  }

  if (!o) return <Modal title="..." onClose={onClose}><div className="empty">Loading...</div></Modal>;
  const commission = o.expected_value && o.owner_pct ? o.expected_value * o.owner_pct / 100 : null;

  return (
    <Modal title={o.title} onClose={onClose} footer={isAdmin && (
      <>
        <button className="btn btn-danger btn-sm" onClick={() => setConfirm(true)}>حذف</button>
        <button className="btn btn-ghost btn-sm" onClick={() => onEdit(o)}>تعديل</button>
      </>
    )}>
      <div dir="rtl">
        <div className="flex gap-8" style={{ flexWrap: 'wrap', marginBottom: 14 }}>
          {STAGES.map(s => (
            <button key={s.key} disabled={!isAdmin}
              className={`btn btn-sm ${o.stage === s.key ? 'btn-primary' : 'btn-ghost'}`}
              style={o.stage === s.key ? { background: s.color, borderColor: s.color } : {}}
              onClick={() => o.stage !== s.key && moveTo(s.key)}>{s.label}</button>
          ))}
        </div>

        {lostAsk && (
          <div className="card" style={{ padding: 12, marginBottom: 12 }}>
            <div className="field"><label>سبب الخسارة</label>
              <input className="input" value={lostReason} onChange={e => setLostReason(e.target.value)} placeholder="سعر، منافس، توقيت..." autoFocus /></div>
            <div className="flex gap-8">
              <button className="btn btn-danger btn-sm" onClick={() => { update({ stage: 'lost', lost_reason: lostReason }); setLostAsk(false); }}>تأكيد</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setLostAsk(false)}>إلغاء</button>
            </div>
          </div>
        )}

        <div className="grid-2 text-sm" style={{ rowGap: 8, marginBottom: 14 }}>
          <div><span className="text-dim">الجهة: </span>{o.client_name || o.org_name || '—'} <span className="text-dim">({ORG_TYPES[o.org_type] || o.org_type})</span></div>
          <div><span className="text-dim">تليفون: </span>{o.contact_phone ? <a className="link" href={`tel:${o.contact_phone}`}>{o.contact_phone}</a> : '—'}</div>
          <div><span className="text-dim">النوع: </span>{OPP_TYPES[o.opp_type] || o.opp_type}</div>
          <div><span className="text-dim">القيمة: </span><span className="fw-600">{money(o.expected_value)}</span></div>
          <div><span className="text-dim">المسؤول: </span>{o.owner || '—'} {o.owner_pct ? `(${o.owner_pct}%)` : ''}</div>
          <div><span className="text-dim">عمولة متوقعة: </span>{money(commission)}</div>
          <div><span className="text-dim">المصدر: </span>{o.source || '—'}</div>
          <div><span className="text-dim">المتابعة: </span>
            <span className={isOverdue(o) ? 'text-red fw-600' : ''}>{o.next_followup ? formatDate(o.next_followup) : '—'}</span>
            {isAdmin && <input type="date" className="input" style={{ display: 'inline-block', width: 'auto', padding: '2px 6px', marginInlineStart: 6 }}
              value={(o.next_followup || '').slice(0, 10)} onChange={e => update({ next_followup: e.target.value })} />}
          </div>
          {o.stage === 'lost' && o.lost_reason && <div><span className="text-dim">سبب الخسارة: </span><span className="text-red">{o.lost_reason}</span></div>}
        </div>
        {o.notes && <p className="text-sm" style={{ whiteSpace: 'pre-wrap', marginBottom: 14 }}>{o.notes}</p>}

        <div className="section-title" style={{ marginBottom: 8 }}>سجل المتابعة</div>
        {isAdmin && (
          <div className="flex gap-8" style={{ marginBottom: 10 }}>
            <select className="select" style={{ width: 'auto' }} value={kind} onChange={e => setKind(e.target.value)}>
              <option value="note">📝 ملاحظة</option>
              <option value="call">📞 مكالمة</option>
              <option value="visit">🚗 زيارة</option>
              <option value="whatsapp">💬 واتساب</option>
            </select>
            <input className="input" value={note} onChange={e => setNote(e.target.value)} placeholder="اكتب اللي حصل..."
              onKeyDown={e => e.key === 'Enter' && addNote()} />
            <button className="btn btn-primary btn-sm" onClick={addNote}>+</button>
          </div>
        )}
        {o.activity.length === 0 ? <p className="text-dim text-sm">لا يوجد</p> : o.activity.map(a => (
          <div key={a.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }} className="text-sm">
            <span>{NOTE_KINDS[a.kind] || '📝'} </span>
            <span className={a.kind === 'system' ? 'text-dim' : ''}>{a.body}</span>
            <div className="text-xs text-dim">{formatDate(a.created_at)}{a.author ? ` · ${a.author}` : ''}</div>
          </div>
        ))}
      </div>
      {confirm && <ConfirmModal message="حذف الفرصة؟" onConfirm={remove} onClose={() => setConfirm(false)} />}
    </Modal>
  );
}

function Card({ o, onOpen, onDragStart }) {
  const overdue = isOverdue(o);
  return (
    <div draggable={!!onDragStart} onDragStart={onDragStart} onClick={onOpen} className="card"
      style={{ padding: 10, marginBottom: 8, cursor: 'pointer', borderInlineStart: `3px solid ${overdue ? 'var(--red)' : isToday(o) ? 'var(--yellow)' : 'transparent'}` }}>
      <div className="fw-600 text-sm">{o.title}</div>
      <div className="text-xs text-muted">{o.client_name || o.org_name || '—'} · {OPP_TYPES[o.opp_type] || o.opp_type}</div>
      <div className="flex items-center gap-8 mt-4" style={{ justifyContent: 'space-between' }}>
        <span className="mono text-xs fw-600">{money(o.expected_value)}</span>
        {o.owner && <span className="text-xs text-dim">{o.owner}</span>}
      </div>
      {o.next_followup && !['won', 'lost'].includes(o.stage) && (
        <div className={`text-xs mt-4 ${overdue ? 'text-red fw-600' : isToday(o) ? 'text-yellow' : 'text-dim'}`}>
          ⏰ {overdue ? 'متأخرة ' : isToday(o) ? 'النهارده ' : ''}{formatDate(o.next_followup)}
        </div>
      )}
    </div>
  );
}

export default function Opportunities() {
  const [opps, setOpps] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [ownerF, setOwnerF] = useState([]);
  const [typeF, setTypeF] = useState([]);
  const [search, setSearch] = useState('');
  const [onlyDue, setOnlyDue] = useState(false);
  const [dragOver, setDragOver] = useState(null);
  const toast = useToast();
  const isAdmin = useIsAdmin();

  const load = useCallback(() => { api.getOpportunities().then(setOpps).catch(e => toast(e.message, 'error')); }, [toast]);
  useEffect(() => {
    load();
    api.getClients().then(setClients).catch(() => {});
    api.getUsers().then(u => setUsers(u.map(x => x.username))).catch(() => {});
  }, [load]);

  const owners = useMemo(() => [...new Set([...users, ...opps.map(o => o.owner).filter(Boolean)])], [users, opps]);

  const filtered = opps.filter(o => {
    if (ownerF.length && !ownerF.includes(o.owner)) return false;
    if (typeF.length && !typeF.includes(o.opp_type)) return false;
    if (onlyDue && !(isOverdue(o) || isToday(o))) return false;
    if (search) {
      const q = search.toLowerCase();
      if (![o.title, o.org_name, o.client_name, o.notes, o.contact_phone].some(v => v && v.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const open = filtered.filter(o => !['won', 'lost'].includes(o.stage));
  const won = filtered.filter(o => o.stage === 'won');
  const closed = won.length + filtered.filter(o => o.stage === 'lost').length;
  const stats = {
    pipeline: open.reduce((s, o) => s + (o.expected_value || 0), 0),
    won: won.reduce((s, o) => s + (o.expected_value || 0), 0),
    rate: closed ? Math.round(won.length / closed * 100) : null,
    overdue: opps.filter(isOverdue).length,
  };

  async function save(data) {
    if (form?.id) { await api.updateOpportunity(form.id, data); toast('اتعدلت'); }
    else { await api.createOpportunity(data); toast('اتضافت'); }
    setForm(null); load();
  }

  async function drop(stage, e) {
    e.preventDefault(); setDragOver(null);
    const id = e.dataTransfer.getData('text/plain');
    const o = opps.find(x => x.id === id);
    if (!o || o.stage === stage) return;
    if (stage === 'lost') { setOpenId(id); return toast('حدد سبب الخسارة', 'error'); }
    setOpps(list => list.map(x => x.id === id ? { ...x, stage } : x));
    try { await api.updateOpportunity(id, { stage, author: me() }); load(); }
    catch (err) { toast(err.message, 'error'); load(); }
  }

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">OPPORTUNITIES <span>_</span></h1>
        {isAdmin && <button className="btn btn-primary" onClick={() => setForm('new')}>+ فرصة</button>}
      </div>

      <div className="stats-grid mb-20">
        <div className="stat-card"><div className="stat-label">Pipeline مفتوح</div><div className="stat-value">{money(stats.pipeline)}</div></div>
        <div className="stat-card"><div className="stat-label">مكسوب</div><div className="stat-value text-green">{money(stats.won)}</div></div>
        <div className="stat-card"><div className="stat-label">معدل الكسب</div><div className="stat-value">{stats.rate === null ? '—' : `${stats.rate}%`}</div></div>
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => setOnlyDue(v => !v)}>
          <div className="stat-label">متابعات متأخرة</div><div className={`stat-value ${stats.overdue ? 'text-red' : ''}`}>{stats.overdue}</div>
        </div>
      </div>

      <div className="flex gap-8 mb-16" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="input" style={{ maxWidth: 240 }} placeholder="🔍 بحث..." value={search} onChange={e => setSearch(e.target.value)} />
        {Object.entries(OPP_TYPES).map(([k, v]) => (
          <button key={k} className={`btn btn-sm ${typeF.includes(k) ? 'btn-primary' : 'btn-ghost'}`} onClick={() => toggle(typeF, setTypeF, k)}>{v}</button>
        ))}
        <span className="text-dim">|</span>
        {owners.map(o => (
          <button key={o} className={`btn btn-sm ${ownerF.includes(o) ? 'btn-primary' : 'btn-ghost'}`} onClick={() => toggle(ownerF, setOwnerF, o)}>👤 {o}</button>
        ))}
        <button className={`btn btn-sm ${onlyDue ? 'btn-danger' : 'btn-ghost'}`} onClick={() => setOnlyDue(v => !v)}>⏰ مستحقة</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${STAGES.length}, minmax(220px, 1fr))`, gap: 12, overflowX: 'auto', paddingBottom: 8 }} dir="rtl">
        {STAGES.map(s => {
          const items = filtered.filter(o => o.stage === s.key);
          const total = items.reduce((t, o) => t + (o.expected_value || 0), 0);
          return (
            <div key={s.key}
              onDragOver={isAdmin ? e => { e.preventDefault(); setDragOver(s.key); } : undefined}
              onDragLeave={() => setDragOver(null)}
              onDrop={isAdmin ? e => drop(s.key, e) : undefined}
              style={{ background: dragOver === s.key ? 'var(--primary-dim)' : 'var(--bg2)', borderRadius: 'var(--radius)', padding: 10, minHeight: 300, borderTop: `3px solid ${s.color}` }}>
              <div className="flex items-center" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
                <span className="fw-600 text-sm">{s.label} <span className="text-dim">({items.length})</span></span>
                <span className="mono text-xs text-dim">{total ? total.toLocaleString('en') : ''}</span>
              </div>
              {items.map(o => (
                <Card key={o.id} o={o} onOpen={() => setOpenId(o.id)}
                  onDragStart={isAdmin ? e => e.dataTransfer.setData('text/plain', o.id) : null} />
              ))}
            </div>
          );
        })}
      </div>

      {form && <OppForm initial={form === 'new' ? {} : form} clients={clients} owners={owners} onSave={save} onClose={() => setForm(null)} />}
      {openId && <OppDetail id={openId} isAdmin={isAdmin} onClose={() => setOpenId(null)} onChanged={load}
        onEdit={o => { setOpenId(null); setForm(o); }} />}
    </div>
  );
}
